import omit from 'lodash/omit';

import {
  Mapping,
  MappingField,
  MappingIdentifier,
  MappingIdentifierType,
  RelationshipMapping,
  Resource,
  ResourceField,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isRelationshipMapping,
  isResource,
} from 'src/containers/ProjectDiagram/utils/data';
import { removeRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/removeRelationshipMapping';
import { getNewFieldsByOldFieldKey } from 'src/containers/ProjectDiagram/utils/resource/getNewFieldsByOldFieldKey';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { ProjectResourceField } from 'src/store/state/projects';

export const updateMappingsForResourceFields =
  ({
    /**
     * if header is changed without escape/quote/separator also being changed, we want
     * to retain references to fields from mappings since we provide default headers
     * (ex: 'a, b, c, d' becomes 'Field 1, Field 2, Field 3, Field4'). Otherwise, new
     * fields are matched with old fields according to rules explained in {@link getNewFieldsByOldFieldKey}
     */
    areFieldReferencesKept,
    newFields,
    oldFields,
    resourceKey,
  }: {
    areFieldReferencesKept: boolean;
    newFields: ProjectResourceField[];
    oldFields: ProjectResourceField[];
    resourceKey: string;
  }) =>
  (model: Model) => {
    const resourceNodeData = model.nodeDataArray.find(
      (nodeData) => nodeData.key === resourceKey
    );
    if (!resourceNodeData || !isResource(resourceNodeData)) {
      return;
    }

    const nextResourceDataFields: ResourceField[] = newFields.map((field) =>
      omit<ProjectResourceField, 'statistics'>(field, 'statistics')
    );
    setDataProperty<Resource>(model)(
      resourceNodeData,
      'fields',
      nextResourceDataFields
    );

    const newFieldsByOldFieldKey = getNewFieldsByOldFieldKey(
      oldFields,
      newFields,
      // attempt to keep field references, but the user might have added a new column
      // to their table for their data source
      areFieldReferencesKept && newFields.length === oldFields.length
    );

    // need to iterate through class mappings first,
    // update fields -> primaryIdentifier & label
    const classMappingsForResource = model.linkDataArray.filter(
      (linkData): linkData is Mapping =>
        isMapping(linkData) && linkData.from === resourceNodeData.key
    );
    const resourceClassMappingsByKey: Record<string, Mapping> = {};
    classMappingsForResource.forEach((linkData) => {
      const updatedMappingFields = Object.values(linkData.fields).reduce<
        Record<string, MappingField>
      >((acc, oldField) => {
        const newField = newFieldsByOldFieldKey[oldField.value];
        if (!newField) {
          return acc;
        }

        acc[newField.value] = {
          label: newField.label,
          value: newField.value,
          targets: oldField.targets,
        };
        return acc;
      }, {});

      const newMappingFields = newFields.reduce<Record<string, MappingField>>(
        (acc, field) => {
          if (!acc[field.value]) {
            acc[field.value] = {
              label: field.label,
              value: field.value,
              targets: [],
            };
          }
          return acc;
        },
        { ...updatedMappingFields }
      );
      const newLabel: string | null =
        linkData.label && newFieldsByOldFieldKey[linkData.label]
          ? newFieldsByOldFieldKey[linkData.label].value
          : null;
      const newMappingIdentifier: MappingIdentifier = {
        ...linkData.primaryIdentifier,
        // if invalid part is found, clear everything
        parts: linkData.primaryIdentifier.parts.some((part) => {
          return (
            part.type === MappingIdentifierType.FIELD &&
            !newFieldsByOldFieldKey[part.value]
          );
        })
          ? []
          : linkData.primaryIdentifier.parts.map((part) =>
              part.type === MappingIdentifierType.FIELD
                ? {
                    type: part.type,
                    value: newFieldsByOldFieldKey[part.value].value,
                  }
                : part
            ),
      };

      resourceClassMappingsByKey[linkData.key] = {
        ...linkData,
        fields: newMappingFields,
        label: newLabel,
        primaryIdentifier: newMappingIdentifier,
      };
      setDataProperty<Mapping>(model)(linkData, 'fields', newMappingFields);
      setDataProperty<Mapping>(model)(linkData, 'label', newLabel);
      setDataProperty<Mapping>(model)(
        linkData,
        'primaryIdentifier',
        newMappingIdentifier
      );

      // remove uniqueKeySets fields that no longer exist
      setDataProperty<Mapping>(model)(
        linkData,
        'uniqueKeySets',
        linkData.uniqueKeySets.filter((fieldValue) =>
          Object.values(newFieldsByOldFieldKey).find(
            ({ value }) => value === fieldValue
          )
        )
      );
    });

    // relationship mappings are deleted completely if they have invalid parts
    // (we don't allow them with no identifier parts).
    // No deletion occurs if fields are being kept (only header changed)
    // TODO: we will allow static text-only rel mappings after VET-5358 however,
    // so we need to update this logic here as well
    const linkDataToRemove: RelationshipMapping[] = [];
    model.linkDataArray.forEach((linkData) => {
      if (!isRelationshipMapping(linkData)) {
        return;
      }

      const classMappingKey = (linkData.from || linkData.to) as string;
      const targetIdentifier =
        linkData.fromPrimaryIdentifier ||
        (linkData.toPrimaryIdentifier as MappingIdentifier);
      const classMapping = resourceClassMappingsByKey[classMappingKey];

      if (!classMapping) {
        return;
      }

      const shouldBeDeleted = targetIdentifier.parts.some((part) => {
        return (
          part.type === MappingIdentifierType.FIELD &&
          !newFieldsByOldFieldKey[part.value]
        );
      });

      if (shouldBeDeleted) {
        linkDataToRemove.push(linkData);
      } else {
        const updatedTargetIdentifier: MappingIdentifier = {
          baseIri: targetIdentifier.baseIri,
          parts: targetIdentifier.parts.map((part) =>
            part.type === MappingIdentifierType.FIELD
              ? {
                  type: part.type,
                  value: newFieldsByOldFieldKey[part.value].value,
                }
              : part
          ),
        };

        if (linkData.fromPrimaryIdentifier) {
          setDataProperty<RelationshipMapping>(model)(
            linkData,
            'fromPrimaryIdentifier',
            updatedTargetIdentifier
          );
        } else if (linkData.toPrimaryIdentifier) {
          setDataProperty<RelationshipMapping>(model)(
            linkData,
            'toPrimaryIdentifier',
            updatedTargetIdentifier
          );
        }
      }
    });
    // do not mutate linkDataArray while iterating through linkDataArray
    linkDataToRemove.forEach((linkData) => {
      removeRelationshipMapping(linkData)(model);
    });
  };
